// Privileged-write authority gate.
//
// ─── Why this exists ──────────────────────────────────────────────────────
//
// Every mutating action in this console — publish, rollback, approve a payout,
// record a treasury transfer, execute a refund, resume settlement — moves money
// or product state. Under GaoSafe those actions are authorised by a THRESHOLD
// of vault signers, not by whoever is holding a browser session.
//
// Phase 1 delivers the first half of that: an operator signs in as a vault
// signer, and the server binds the session to the vault. It deliberately does
// NOT deliver the approval engine — there is no proposal builder, no signature
// collection, no threshold execution. So every privileged write is frozen, and
// the console must say so in the one term that is actually true:
//
//     GAOSAFE APPROVAL REQUIRED
//
// Not "not permitted" (the operator IS a vault owner), not "coming soon", and
// not a generic disabled button. Being a signer authenticates you; it does not
// on its own authorise a write.
//
// ─── Why a context rather than a flag ─────────────────────────────────────
//
// The default is BLOCKED and the shipped application never renders a provider,
// so writes cannot be re-enabled in the bundle by flipping a variable, by an
// env value, or by anything an operator can reach at runtime. Re-enabling is a
// source change that has to add a provider — which is reviewable.
//
// Tests that exercise a write FLOW wrap their render in
// `<AuthorityProvider allowWrites>`. That keeps the regression coverage for
// those flows alive and intact for the day the approval engine turns them back
// on, instead of deleting it and having to rediscover it later.
//
// ─── What this does NOT do ────────────────────────────────────────────────
//
// It changes no endpoint, no auth protocol, no session semantic and no backend
// policy. `src/api/*`, `src/auth/*`, `src/controlPlane/api.ts` and
// `src/worker.ts` are untouched. This gate makes privileged writes unreachable
// from the user interface; it is not, and must not be mistaken for, a server
// -side control. The server's own `FEATURE_ADMIN_WRITES` write gate is unset
// and therefore closed, and THAT is the boundary. This is the half that keeps
// the console from offering a button whose only possible outcome is a 403.

import { createContext, useContext, type ReactNode } from "react";
import { Banner, Chip } from "./ui/primitives";

/** Shown wherever a write is gated. The operator-specified wording, verbatim. */
export const GAOSAFE_APPROVAL_LABEL = "GAOSAFE APPROVAL REQUIRED";

export const GAOSAFE_APPROVAL_REASON =
  "Privileged writes require GaoSafe threshold approval. Signing in as a vault signer authenticates you; it does not authorise this action.";

const AuthorityContext = createContext<{ allowWrites: boolean }>({
  // BLOCKED by default. The application renders no provider, so this is the
  // value the shipped console always sees.
  allowWrites: false,
});

export function AuthorityProvider({
  allowWrites = false,
  children,
}: {
  allowWrites?: boolean;
  children: ReactNode;
}) {
  return (
    <AuthorityContext.Provider value={{ allowWrites }}>{children}</AuthorityContext.Provider>
  );
}

/** True when privileged writes must be presented as unavailable. */
export function useWritesBlocked(): boolean {
  return !useContext(AuthorityContext).allowWrites;
}

/** The concise per-control state.
 *
 *  Rendered beside a control the gate has disabled, on surfaces that do not
 *  carry an `AuthorityNotice` — a modal, or a form whose only mutation is the
 *  button it sits next to. The tooltip carries the sentence; the chip carries
 *  the state, because a `title` attribute is not something an operator sees. */
export function ApprovalRequiredChip() {
  return (
    <Chip tone="warn" size="sm" title={GAOSAFE_APPROVAL_REASON}>
      {GAOSAFE_APPROVAL_LABEL}
    </Chip>
  );
}

/** The standing notice for a screen that owns one or more gated actions.
 *
 *  Rendered with no ARIA role on purpose. Twenty assertions across nine test
 *  files match `getByRole("alert")` / `getByRole("status")` in the SINGULAR,
 *  and Testing Library throws on a second match — a page-level live region
 *  here would break every one of them. The chip beside each disabled control
 *  is what carries the per-action meaning; this is the explanation. */
export function AuthorityNotice({ scope }: { scope?: string }) {
  return (
    <Banner tone="warn" role="none" title={GAOSAFE_APPROVAL_LABEL}>
      {GAOSAFE_APPROVAL_REASON}
      {scope !== undefined && ` ${scope}`} Read-only views are unaffected.
    </Banner>
  );
}
